import { useState } from 'react';
import { supabase } from '../supabase';

export interface UseProjectMembership {
  busy: boolean;
  error: string | null;
  /** Assign a ROOT work item to a project (or null to clear). The cascade trigger handles its subtree. */
  setWorkItemProject: (orgId: string, workItemId: string, projectId: string | null) => Promise<boolean>;
  linkDocument: (orgId: string, projectId: string, documentId: string) => Promise<boolean>;
  unlinkDocument: (projectId: string, documentId: string) => Promise<boolean>;
}

export function useProjectMembership(): UseProjectMembership {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (
    fn: () => PromiseLike<{ error: { message: string } | null }>
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const { error: e } = await fn();
      if (e) {
        setError(e.message);
        return false;
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error');
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    error,
    setWorkItemProject: (_orgId, workItemId, projectId) =>
      // Goes through the SECURITY DEFINER RPC so the change lands in the work
      // item's Activity feed (audit_events). The RPC scopes via wi_assert_member,
      // so orgId is no longer needed for the write.
      run(() =>
        supabase.rpc('set_work_item_project', {
          p_work_item_id: workItemId,
          p_project_id: projectId
        })
      ),
    linkDocument: (orgId, projectId, documentId) =>
      run(() =>
        supabase
          .from('project_documents')
          .insert({ org_id: orgId, project_id: projectId, document_id: documentId })
      ),
    unlinkDocument: (projectId, documentId) =>
      run(() =>
        supabase
          .from('project_documents')
          .delete()
          .eq('project_id', projectId)
          .eq('document_id', documentId)
      )
  };
}
